/**
 * InductionApproved - Post-approval welcome page
 * Route: /i/:token/approved
 *
 * Visitors land here via the link in their approval email.
 * Shows a warm welcome, their digital pass QR code, and
 * instructions for their site visit.
 */

import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getInvite, getApprovedPass, parseRpcError } from '@shared/utils/publicRpc';
import { useInviteToken } from '@shared/utils/inviteToken';

export default function InductionApproved() {
  const token = useInviteToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [pass, setPass] = useState(null);

  useEffect(() => {
    if (!token) return;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        // Load in parallel — pass fetch is optional (fails gracefully)
        const [inviteData, passData] = await Promise.allSettled([
          getInvite(token),
          getApprovedPass(token),
        ]);

        if (inviteData.status === 'fulfilled' && inviteData.value) {
          setInvite(inviteData.value);
        } else {
          setError('Unable to load your details. Please check the link in your email.');
        }

        if (passData.status === 'fulfilled' && passData.value) {
          setPass(passData.value);
        }
        // If pass fetch fails we still show the welcome — pass may not be available yet
      } catch (err) {
        setError(parseRpcError(err));
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [token]);

  /* ── Loading ─────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent" />
          <p className="mt-4 text-gray-600">Loading your pass...</p>
        </div>
      </div>
    );
  }

  /* ── Error ───────────────────────────────────────────── */
  if (error && !invite) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Unable to Load Pass</h2>
        <p className="mt-2 text-gray-600 max-w-sm mx-auto">{error}</p>
        <p className="mt-4 text-sm text-gray-500">
          If your pass has been approved, it was emailed to you. Please check your inbox.
        </p>
      </div>
    );
  }

  const firstName = invite?.person_name?.split(' ')[0] || 'there';
  const siteName = pass?.site_name || invite?.brief_title?.replace('Site Induction', '').trim() || 'the site';
  const validUntil = pass?.valid_until
    ? new Date(pass.valid_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const approvedAt = pass?.approved_at
    ? new Date(pass.approved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">

      {/* ── Celebration banner ─────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden shadow-md">
        {/* Green header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-8 py-8 text-white">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                Welcome, {firstName}!
              </h1>
              <p className="mt-1 text-green-100 text-sm">
                Your site induction has been reviewed and approved.
              </p>
            </div>
          </div>
        </div>

        {/* Message body */}
        <div className="bg-white px-8 py-6">
          <p className="text-gray-700 leading-relaxed">
            Thank you for completing your site induction. We look forward to welcoming you on your
            visit{siteName ? ` to ${siteName}` : ''}. Please keep this page accessible on arrival —
            your digital pass is shown below.
          </p>

          {invite && (
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs text-gray-500">Name</p>
                <p className="text-sm font-semibold text-gray-900">{invite.person_name}</p>
              </div>
              {invite.person_company && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500">Company</p>
                  <p className="text-sm font-semibold text-gray-900">{invite.person_company}</p>
                </div>
              )}
              {approvedAt && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500">Approved on</p>
                  <p className="text-sm font-semibold text-gray-900">{approvedAt}</p>
                </div>
              )}
              {validUntil && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500">Pass valid until</p>
                  <p className="text-sm font-semibold text-gray-900">{validUntil}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Digital Pass / QR ─────────────────────────────── */}
      {pass?.pass_token ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5a.5.5 0 11-1 0 .5.5 0 011 0z"/>
            </svg>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-widest">
              Your Digital Pass
            </h2>
          </div>
          <div className="flex flex-col items-center px-8 py-8 bg-gray-50">
            <div className="rounded-xl border-4 border-white shadow-lg p-4 bg-white">
              <QRCodeSVG
                value={pass.pass_token}
                size={200}
                level="M"
                includeMargin={false}
              />
            </div>
            <p className="mt-4 text-xs text-gray-500 text-center">
              Present this QR code at the site entry kiosk to check in.
            </p>
            <p className="mt-1 text-xs text-gray-400 font-mono tracking-widest">
              {pass.pass_token}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-6 py-5">
          <div className="flex items-start gap-3">
            <svg className="flex-shrink-0 h-5 w-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"/>
            </svg>
            <div>
              <p className="text-sm font-semibold text-blue-800">Pass emailed to you</p>
              <p className="mt-1 text-sm text-blue-700">
                Your digital pass and QR code have been sent to <strong>{invite?.person_email}</strong>.
                Check your inbox (and spam folder) for an email with your pass attached.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Visit Instructions ────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-widest mb-4">
          On the Day of Your Visit
        </h2>
        <ol className="space-y-4">
          {[
            {
              icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
              title: 'Have your pass ready',
              desc: 'Keep this page open on your phone or bring your pass from the email.',
            },
            {
              icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
              title: 'Check in at the kiosk',
              desc: 'Scan your QR code at the entry kiosk when you arrive at site.',
            },
            {
              icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
              title: 'Follow all site safety rules',
              desc: 'Adhere to the safety requirements you agreed to in your induction at all times.',
            },
            {
              icon: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
              title: 'Check out when leaving',
              desc: 'Scan your QR code again at the kiosk when you leave site.',
            },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-4 w-4 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* ── Footer ────────────────────────────────────────── */}
      <div className="text-center text-xs text-gray-400 pb-4">
        <p>Having problems? Contact the site office and quote your reference.</p>
        {pass?.pass_token && (
          <p className="mt-1 font-mono tracking-widest">{pass.pass_token}</p>
        )}
      </div>

    </div>
  );
}
